import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../api/axiosConfig';

// Admin-only maintenance of the managed category list. Kept deliberately plain:
// it is configuration touched a few times a year, not a daily screen.
//
// Deactivating is the safe default — it hides a category from new payments
// while leaving the existing ones intact. Deleting is only offered for
// non-default categories that nothing references; the backend refuses the rest.

const PRESET_COLORS = [
    { value: '#3B82F6', name: 'Blue' },
    { value: '#F59E0B', name: 'Amber' },
    { value: '#06B6D4', name: 'Cyan' },
    { value: '#14B8A6', name: 'Teal' },
    { value: '#8B5CF6', name: 'Purple' },
    { value: '#10B981', name: 'Green' },
    { value: '#EC4899', name: 'Pink' },
    { value: '#F97316', name: 'Orange' },
    { value: '#6B7280', name: 'Grey' }
];

// Picking beats typing an emoji on a desktop keyboard; the text box stays for anything else
const PRESET_ICONS = ['📦', '💻', '🤖', '📣', '☁️', '🧩', '💡', '🏢', '🛡️', '🖨️', '🔧', '🚚', '📱', '🌐', '🎓', '🧾'];

const PaymentCategoryManager = ({ onClose, onChanged }) => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [newCategory, setNewCategory] = useState({ name: '', icon: '📦', color: '#6B7280' });
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/payment-categories?includeInactive=true');
            if (res.data.success) setCategories(res.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load categories');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleAdd = async (e) => {
        e.preventDefault();
        setError('');
        if (!newCategory.name.trim()) return;
        setBusy(true);
        try {
            await axiosInstance.post('/api/payment-categories', newCategory);
            setNewCategory({ name: '', icon: '📦', color: '#6B7280' });
            await load();
            onChanged?.();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add category');
        } finally {
            setBusy(false);
        }
    };

    const toggleActive = async (category) => {
        setError('');
        try {
            await axiosInstance.put(`/api/payment-categories/${category._id}`, { isActive: !category.isActive });
            await load();
            onChanged?.();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update category');
        }
    };

    const handleDelete = async (category) => {
        if (!window.confirm(`Delete the category "${category.name}"?`)) return;
        setError('');
        try {
            await axiosInstance.delete(`/api/payment-categories/${category._id}`);
            await load();
            onChanged?.();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete category');
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Payment Categories</h2>
                    <button type="button" className="modal-close" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    {error && <div className="alert alert-error">{error}</div>}
                    <p className="form-hint">
                        Categories group payments for reporting. Deactivate one to hide it from new
                        payments without changing the ones already filed under it.
                    </p>

                    <form className="category-add" onSubmit={handleAdd}>
                        <div className="category-add-title">New category</div>

                        {/* Live preview — exactly how the row will look in the list */}
                        <div className="category-preview">
                            <span
                                className="pay-card-icon pay-card-icon-sm"
                                style={{ background: `${newCategory.color}1F`, color: newCategory.color }}
                            >
                                {newCategory.icon || '📦'}
                            </span>
                            <span className={newCategory.name.trim() ? 'category-name' : 'category-preview-empty'}>
                                {newCategory.name.trim() || 'Your category'}
                            </span>
                        </div>

                        <label className="category-field-label" htmlFor="new-category-name">Name</label>
                        <input
                            id="new-category-name"
                            className="form-input"
                            value={newCategory.name}
                            onChange={(e) => setNewCategory(p => ({ ...p, name: e.target.value }))}
                            placeholder="e.g. Training"
                        />

                        <span className="category-field-label">Icon</span>
                        <div className="icon-picker">
                            {PRESET_ICONS.map(icon => (
                                <button
                                    key={icon}
                                    type="button"
                                    className={`icon-choice ${newCategory.icon === icon ? 'icon-choice-active' : ''}`}
                                    onClick={() => setNewCategory(p => ({ ...p, icon }))}
                                    aria-label={`Icon ${icon}`}
                                    aria-pressed={newCategory.icon === icon}
                                >
                                    {icon}
                                </button>
                            ))}
                            <input
                                className="form-input icon-custom"
                                value={PRESET_ICONS.includes(newCategory.icon) ? '' : newCategory.icon}
                                onChange={(e) => setNewCategory(p => ({ ...p, icon: e.target.value }))}
                                maxLength={4}
                                placeholder="Other"
                                aria-label="Type or paste your own icon"
                                title="Type or paste any emoji"
                            />
                        </div>

                        <span className="category-field-label">
                            Colour <span className="form-hint">— {PRESET_COLORS.find(c => c.value === newCategory.color)?.name}</span>
                        </span>
                        <div className="color-picker-row">
                            {PRESET_COLORS.map(({ value, name }) => (
                                <button
                                    key={value}
                                    type="button"
                                    className={`color-dot ${newCategory.color === value ? 'color-dot-active' : ''}`}
                                    style={{ background: value }}
                                    onClick={() => setNewCategory(p => ({ ...p, color: value }))}
                                    aria-label={name}
                                    aria-pressed={newCategory.color === value}
                                    title={name}
                                />
                            ))}
                        </div>

                        <button type="submit" className="pay-btn pay-btn-maroon category-add-btn" disabled={busy || !newCategory.name.trim()}>
                            {busy ? 'Adding…' : '+ Add category'}
                        </button>
                    </form>

                    <div className="category-add-title">Existing categories</div>

                    {loading ? (
                        <div className="loading-state">Loading...</div>
                    ) : (
                        <div className="category-list">
                            {categories.map(category => (
                                <div key={category._id} className={`category-row ${category.isActive ? '' : 'category-row-inactive'}`}>
                                    <span
                                        className="pay-card-icon pay-card-icon-sm"
                                        style={{ background: `${category.color}1F`, color: category.color }}
                                    >
                                        {category.icon}
                                    </span>
                                    <div className="category-text">
                                        <span className="category-name">{category.name}</span>
                                        <span className="category-count">
                                            {category.usageCount || 0} payment{category.usageCount === 1 ? '' : 's'}
                                            {!category.isActive && ' · inactive'}
                                        </span>
                                    </div>
                                    <div className="category-actions">
                                        <button
                                            className={`category-btn ${category.isActive ? '' : 'category-btn-on'}`}
                                            onClick={() => toggleActive(category)}
                                        >
                                            {category.isActive ? 'Deactivate' : 'Reactivate'}
                                        </button>
                                        {!category.isSystem && !category.usageCount && (
                                            <button className="category-btn category-btn-danger" onClick={() => handleDelete(category)}>
                                                Delete
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

export default PaymentCategoryManager;
